// PROMPT AUDIT — what system prompt a seat actually ran on, recorded as names, byte
// counts and hashes only (plan 101 slice 01). The TEXT is never stored: tenant prose
// (org/repo/user layers) may hold secrets, and a hash is enough to prove two runs saw
// the same prompt and to localise which layer changed between them.
//
// ONE implementation for every composer (in-cluster `agents-manager`, the fleet box's
// worker/review/scout seats), so a hash taken on one side is comparable to the other.

// Namespace import: binds the module without touching any property at eval time.
// `@developerz/domain` is evaluated by the browser dashboard, where `node:crypto`
// is a stub that throws on property access; these functions are only ever CALLED
// server-side (`browser-evaluability.test.ts`, the `authored-task-id.ts` posture).
import * as nodeCrypto from 'node:crypto';

import { z } from 'zod';

/** One named layer of a composed prompt (`platform`, `role`, `org`, `repo`, `loop`, …). */
export type PromptPart = { readonly name: string; readonly text: string };

const Hex64Schema = z.string().regex(/^[0-9a-f]{64}$/);

export const PromptLayerRecordSchema = z
  .object({
    name: z.string().min(1),
    /** UTF-8 bytes of the layer text, not UTF-16 code units. */
    bytes: z.number().int().nonnegative(),
    sha256: Hex64Schema,
  })
  .strict();
export type PromptLayerRecord = z.infer<typeof PromptLayerRecordSchema>;

/**
 * What one composed system prompt was: its hash and bytes, the version set of the
 * built-in prompt files, and its layers.
 *
 * KNOWN GAP — the hash covers the system prompt a COMPOSER built, not every byte a
 * provider sees. `@developerz/agent`'s json-prompt middleware (`providers/json-prompt.ts`,
 * mounted by the provider modules that import it — groq, alibaba, minimax, deepseek, zai,
 * openrouter, some only for specific model families) PREPENDS its own system message on
 * structured-output calls: a JSON instruction carrying the call's JSON Schema. That
 * message is in no audit, so on those wires equal `prompt_sha256` proves the same
 * composed prompt, not the same full request.
 */
export const PromptAuditSchema = z
  .object({
    /** sha256 of the full text SENT to the model. */
    prompt_sha256: Hex64Schema,
    prompt_bytes: z.number().int().nonnegative(),
    /**
     * The producing lane's prompt set id (12-hex): `PROMPT_VERSION_SET_ID` (the coding
     * lane) or, for screen asks, the screen lane's id; null when unknown.
     */
    prompt_version_set_id: z
      .string()
      .regex(/^[0-9a-f]{12}$/)
      .nullable(),
    /** Non-empty layers, in composition order. */
    layers: z.array(PromptLayerRecordSchema),
  })
  .strict();
export type PromptAudit = z.infer<typeof PromptAuditSchema>;

/** The composers' layer separator. */
const PROMPT_PART_SEPARATOR = '\n\n';

/** Parts joined with the composers' separator — the ONE spelling of "these layers ARE this prompt". */
export function joinPromptParts(parts: readonly PromptPart[]): string {
  return parts.map((p) => p.text).join(PROMPT_PART_SEPARATOR);
}

const utf8 = new TextEncoder();

/** UTF-8 byte length (same count as `Buffer.byteLength`, but browser-evaluable). */
function utf8Bytes(text: string): number {
  return utf8.encode(text).length;
}

function digestText(text: string): string {
  return nodeCrypto.createHash('sha256').update(text, 'utf8').digest('hex');
}

function nonEmpty(parts: readonly PromptPart[]): readonly PromptPart[] {
  return parts.filter((p) => p.text.length > 0);
}

function layerRecords(parts: readonly PromptPart[]): PromptLayerRecord[] {
  return parts.map((p) => ({
    name: p.name,
    bytes: utf8Bytes(p.text),
    sha256: digestText(p.text),
  }));
}

/**
 * Joins non-empty parts with '\n\n' (the composers' separator), hashes each part and
 * the join. Text is never stored. Empty parts are omitted from `layers` and the join.
 */
export function promptAuditOf(
  parts: readonly PromptPart[],
  versionSetId: string | null,
): PromptAudit {
  const kept = nonEmpty(parts);
  const text = joinPromptParts(kept);
  return promptAuditOfText(text, kept, versionSetId);
}

/**
 * For callers whose final text is already joined differently: hash the given full
 * text, record the (non-empty) parts. The result is `parse`d, so a malformed version
 * set id or an empty layer name throws here, at the seam, not at the writer.
 */
export function promptAuditOfText(
  text: string,
  parts: readonly PromptPart[],
  versionSetId: string | null,
): PromptAudit {
  return PromptAuditSchema.parse({
    prompt_sha256: digestText(text),
    prompt_bytes: utf8Bytes(text),
    prompt_version_set_id: versionSetId,
    layers: layerRecords(nonEmpty(parts)),
  });
}
